import { useCallback, useMemo } from "react";
import { useSetVendorTags, useSponsorVendorTags } from "../custom-hooks/queries";

/**
 * Per-vendor-per-sponsor custom tags backed by SponsorService API.
 * Org-wide tag set is derived as the union of all vendor tag rows for the sponsor.
 */
export function useVendorTags(sponsorId = null) {
  const { data, isLoading, isError } = useSponsorVendorTags(sponsorId);
  const setTagsMutation = useSetVendorTags(sponsorId);

  const vendorTagsMap = useMemo(
    () => data?.vendor_tags || {},
    [data?.vendor_tags]
  );

  const allKnownTags = useMemo(
    () => data?.all_known_tags || [],
    [data?.all_known_tags]
  );

  const getTagListFor = useCallback(
    (providerId) => {
      if (!providerId) return [];
      const key = String(providerId);
      return vendorTagsMap[key] || [];
    },
    [vendorTagsMap]
  );

  const setTagsFor = useCallback(
    (providerId, tags) => {
      if (!providerId) return;
      setTagsMutation.mutate({ providerId, tags });
    },
    [setTagsMutation]
  );

  const addTag = useCallback(
    (providerId, tag) => {
      const cleaned = String(tag || "").trim();
      if (!cleaned || !providerId) return;
      const existing = getTagListFor(providerId);
      if (existing.some((t) => t.toLowerCase() === cleaned.toLowerCase())) return;
      setTagsFor(providerId, [...existing, cleaned]);
    },
    [getTagListFor, setTagsFor]
  );

  const removeTag = useCallback(
    (providerId, tag) => {
      const cleaned = String(tag || "").trim();
      if (!cleaned || !providerId) return;
      const existing = getTagListFor(providerId);
      setTagsFor(
        providerId,
        existing.filter((t) => t.toLowerCase() !== cleaned.toLowerCase())
      );
    },
    [getTagListFor, setTagsFor]
  );

  return {
    allKnownTags,
    vendorTagsMap,
    getTagListFor,
    setTagsFor,
    addTag,
    removeTag,
    isLoading,
    isError,
    isSaving: setTagsMutation.isPending,
    sponsorId: data?.sponsor_id ?? sponsorId,
  };
}

export default useVendorTags;
